"use client"

import React, { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { ArrowBigLeft, Printer } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

const OrderEstimate = () => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const invoiceRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const data = searchParams.get("data") || null;
  function generateOrderNumber() {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    const random = Math.floor(10000 + Math.random() * 90000); // 5-digit random number
    return `ORDER-${date}-${random}`;
  }
  useEffect(() => {
    try {
      // In a real application, you would parse the URL query parameters
      // For this example, we'll use the sample data directly
      const sampleData = localStorage.getItem('invoice') || JSON.stringify({
        _id: "ESTM-" + Date.now().toString().substring(6),
        additionalCharges: { $numberDecimal: "1" },
        advanceAmount: { $numberDecimal: "2000" },
        amountWithGST: { $numberDecimal: "3.54" },
        area: "Airport Road",
        city: "Mandsaur",
        createdAt: new Date().toISOString(),
        dispatchMode: "TRAIN",
        flat: "2",
        gstNumber: "CBCPJ1260Q",
        gstPercentage: "18",
        leadId: { 
          name: "Vijay Kumawat", 
          _id: "68051a2ea2ee0cfa804d2023" 
        },
        orderPriority: "MEDIUM",
        packagingCharges: { $numberDecimal: "1" },
        pincode: "452005",
        primaryPhone: "9926677102",
        products: [{
          productCode: "KEY",
          quantity: 1,
          rate: 1,
          size: "16102",
          gstPercentage: "18"
        }],
        state: "madhya Pradesh"
      });
      
      setInvoice(JSON.parse(data));
    } catch (error) {
      console.error("Error parsing invoice data:", error);
    } finally {
      setLoading(false);
    }
  }, [data]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;

    try {
      // Apply print styles temporarily for better PDF generation
      invoiceRef.current.classList.add("print:p-2", "print:text-sm", "print:shadow-none", "print:border-0");

      const canvas = await html2canvas(invoiceRef.current, {
        scale: 1.5, // Reduced scale for better fit
        logging: false,
        useCORS: true,
      });

      // Remove temporary styles
      invoiceRef.current.classList.remove("print:p-2", "print:text-sm", "print:shadow-none", "print:border-0");

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Calculate dimensions to fit on a single page
      const imgWidth = 190; // slightly smaller than A4 width (210mm)
      const pageHeight = 297; // A4 height
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Ensure it fits on a single page
      const scaleFactor = Math.min(1, pageHeight / imgHeight);
      const finalImgWidth = imgWidth * scaleFactor;
      const finalImgHeight = imgHeight * scaleFactor;

      pdf.addImage(imgData, "PNG", 10, 10, finalImgWidth, finalImgHeight);
      pdf.save(`order_estimate_${invoice?._id || 'new'}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (!invoice) {
    return <div className="flex justify-center items-center h-screen">No estimate data found</div>;
  }

  // Safely parse numerical values with fallbacks to prevent NaN
  const getNumberValue = (value, defaultValue = 0) => {
    if (!value) return defaultValue;
    
    if (typeof value === 'number') return value;
    
    if (value.$numberDecimal) {
      return Number.parseFloat(value.$numberDecimal) || defaultValue;
    }
    
    return Number.parseFloat(value) || defaultValue;
  };

  // Calculate totals with safe number parsing
  const subtotal = invoice.products?.reduce((acc, product) => {
    const quantity = product.quantity || 0;
    const rate = product.rate || 0;
    return acc + (quantity * rate);
  }, 0) || 0;

  const additionalCharges = getNumberValue(invoice.additionalCharges);
  const packagingCharges = getNumberValue(invoice.packagingCharges);
  const totalBeforeGST = subtotal + additionalCharges + packagingCharges;

  // Only apply GST to product subtotal, not to packaging and additional charges
  const gstPercentage = getNumberValue(invoice.gstPercentage);
  const gstAmount = (subtotal * gstPercentage) / 100;
  const cgstAmount = gstAmount / 2;
  const sgstAmount = gstAmount / 2;

  // Recalculate total amount with the new GST calculation
  const calculatedTotalAmount = totalBeforeGST + gstAmount;
  const totalAmount = getNumberValue(invoice.amountWithGST, calculatedTotalAmount);

  const advanceAmount = getNumberValue(invoice.advanceAmount);
  const balanceAmount = totalAmount - advanceAmount;

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  };

  // Convert number to words
  const numberToWords = (num) => {
    const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const teens = [
      "",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    if (num === 0) return "Zero";

    const convertLessThanThousand = (n) => {
      if (n === 0) return "";

      let result = "";

      if (n >= 100) {
        result += units[Math.floor(n / 100)] + " Hundred ";
        n %= 100;
      }

      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + " ";
        n %= 10;
      } else if (n >= 11 && n <= 19) {
        result += teens[n - 10] + " ";
        n = 0;
      }

      if (n > 0) {
        result += units[n] + " ";
      }

      return result;
    };

    let result = "";

    if (num >= 100000) {
      result += convertLessThanThousand(Math.floor(num / 100000)) + "Lakh ";
      num %= 100000;
    }

    if (num >= 1000) {
      result += convertLessThanThousand(Math.floor(num / 1000)) + "Thousand ";
      num %= 1000;
    }

    result += convertLessThanThousand(num);

    return result.trim();
  };

  const getLeadName = () => {
    if (!invoice.name) return "";
    if (typeof invoice.name === 'string') return invoice.name || "";
    return invoice.name || "";
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="mb-4 flex justify-end space-x-2 print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2"
        >
          <ArrowBigLeft size={18} />
          Back
        </button>
        <button
          onClick={handlePrint}
          className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2"
        >
          <Printer size={18} />
          Print Estimate
        </button>
        <button
          onClick={handleDownloadPDF}
          className="bg-green-600 text-white px-4 py-2 rounded"
        >
          Download PDF
        </button>
      </div>

      <div
        ref={invoiceRef}
        className="bg-white p-6 border border-gray-300 shadow-md print:p-2 print:shadow-none print:border-0 print:text-sm"
      >
        {/* Estimate Header */}
        <div className="text-center border-b-2 border-gray-800 pb-4">
          <h1 className="text-2xl font-bold">ORDER ESTIMATE</h1>
          <p className="text-sm">(ORIGINAL FOR RECIPIENT)</p>
        </div>

        {/* Company Details */}
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs print:text-[10px]">
          <div>
            <h2 className="font-bold">Printverse Pvt. Ltd.</h2>
            <p>20 JUNA TUKOGANJ</p>
            <p>OPP. RAMBAGH PETROL PUMP</p>
            <p>INDORE 452007</p>
            <p>MOB 7389925002, 7036231237</p>
            <p>GSTIN/UIN: 23AANCP1856B1ZM</p>
            <p>State Name: Madhya Pradesh, Code: 23</p>
            <p>E-Mail: accounts@printverse.com</p>
          </div>
          <div>
            <h3 className="font-bold">Buyer (Bill to)</h3>
            <p>{getLeadName()}</p>
            <p>{invoice.flat || ""}{invoice.flat && invoice.area ? ", " : ""}{invoice.area || ""}</p>
            <p>
              {invoice.city || ""}{invoice.city && invoice.pincode ? ", " : ""}{invoice.pincode || ""}
            </p>
            <p>{invoice.primaryPhone || ""}</p>
            {invoice.gstNumber && <p>GSTIN: {invoice.gstNumber}</p>}
            {invoice.state && <p>State Name: {invoice.state}, Code: 23</p>}
          </div>
        </div>

        {/* Invoice Details */}
        <div className="mt-4 grid grid-cols-2 gap-4 border-t border-b border-gray-300 py-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <p className="font-semibold">Estimate No.</p>
            <p>{invoice._id || "EST-" + Date.now().toString().substring(6)}</p>

            <p className="font-semibold">Dated</p>
            <p>{formatDate(new Date())}</p>

           
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <p className="font-semibold">Buyer's Order No.</p>
            <p>{generateOrderNumber()}</p>


            <p className="font-semibold">Dispatched through</p>
            <p>{invoice.dispatchMode || ""}</p>

            <p className="font-semibold">Destination</p>
            <p>{invoice.city || ""}</p>
          </div>
        </div>

        {/* Products Table */}
        <div className="mt-4">
          <table className="w-full border-collapse text-xs print:text-[10px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-1 text-left">Sl No.</th>
                <th className="border border-gray-300 p-1 text-left">Description of Goods</th>
                <th className="border border-gray-300 p-1 text-left">HSN/SAC</th>
                <th className="border border-gray-300 p-1 text-left">GST</th>
                <th className="border border-gray-300 p-1 text-left">Quantity</th>
                <th className="border border-gray-300 p-1 text-left">Rate</th>
                <th className="border border-gray-300 p-1 text-left">per</th>
                <th className="border border-gray-300 p-1 text-left">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.products && invoice.products.map((product, index) => {
                const quantity = product.quantity || 0;
                const rate = product.rate || 0;
                const amount = quantity * rate;
                const productGstPercentage = product.gstPercentage || invoice.gstPercentage || "0";

                return (
                  <tr key={index}>
                    <td className="border border-gray-300 p-1">{index + 1}</td>
                    <td className="border border-gray-300 p-1">{product.productCode || "Product"}</td>
                    <td className="border border-gray-300 p-1">3920</td>
                    <td className="border border-gray-300 p-1">{productGstPercentage} %</td>
                    <td className="border border-gray-300 p-1">{quantity} pcs</td>
                    <td className="border border-gray-300 p-1">{rate.toFixed(2)}</td>
                    <td className="border border-gray-300 p-1">pcs</td>
                    <td className="border border-gray-300 p-1">{amount.toFixed(2)}</td>
                  </tr>
                );
              })}

              {/* Additional Charges */}
              {additionalCharges > 0 && (
                <tr>
                  <td className="border border-gray-300 p-1">{(invoice.products?.length || 0) + 1}</td>
                  <td className="border border-gray-300 p-1">Additional Charges</td>
                  <td className="border border-gray-300 p-1">9997</td>
                  <td className="border border-gray-300 p-1">0 %</td>
                  <td className="border border-gray-300 p-1">1</td>
                  <td className="border border-gray-300 p-1">{additionalCharges.toFixed(2)}</td>
                  <td className="border border-gray-300 p-1">-</td>
                  <td className="border border-gray-300 p-1">{additionalCharges.toFixed(2)}</td>
                </tr>
              )}

              {/* Packaging Charges */}
              {packagingCharges > 0 && (
                <tr>
                  <td className="border border-gray-300 p-1">
                    {(invoice.products?.length || 0) + (additionalCharges > 0 ? 2 : 1)}
                  </td>
                  <td className="border border-gray-300 p-1">Packaging Charges</td>
                  <td className="border border-gray-300 p-1">9997</td>
                  <td className="border border-gray-300 p-1">0 %</td>
                  <td className="border border-gray-300 p-1">1</td>
                  <td className="border border-gray-300 p-1">{packagingCharges.toFixed(2)}</td>
                  <td className="border border-gray-300 p-1">-</td>
                  <td className="border border-gray-300 p-1">{packagingCharges.toFixed(2)}</td>
                </tr>
              )}

              {/* Empty row for spacing */}
              <tr>
                <td colSpan={7} className="border border-gray-300 p-1 text-right font-semibold">
                  Subtotal
                </td>
                <td className="border border-gray-300 p-1">{totalBeforeGST.toFixed(2)}</td>
              </tr>

              {/* GST Rows */}
              <tr>
                <td colSpan={7} className="border border-gray-300 p-1 text-right font-semibold">
                  CGST
                </td>
                <td className="border border-gray-300 p-1">{cgstAmount.toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan={7} className="border border-gray-300 p-1 text-right font-semibold">
                  SGST
                </td>
                <td className="border border-gray-300 p-1">{sgstAmount.toFixed(2)}</td>
              </tr>

              {/* Round Off */}
              <tr>
                <td colSpan={7} className="border border-gray-300 p-1 text-right font-semibold">
                  Round Off
                </td>
                <td className="border border-gray-300 p-1">
                  {(Math.round(calculatedTotalAmount) - calculatedTotalAmount).toFixed(2)}
                </td>
              </tr>

              {/* Total */}
              <tr className="bg-gray-100">
                <td colSpan={7} className="border border-gray-300 p-1 text-right font-bold">
                  Total
                </td>
                <td className="border border-gray-300 p-1 font-bold">
                  ₹ {Math.round(calculatedTotalAmount).toFixed(2)}
                </td>
              </tr>

              {/* Advance Amount */}
              {advanceAmount > 0 && (
                <tr>
                  <td colSpan={7} className="border border-gray-300 p-1 text-right font-semibold">
                    Advance Amount
                  </td>
                  <td className="border border-gray-300 p-1">₹ {advanceAmount.toFixed(2)}</td>
                </tr>
              )}

              {/* Balance Amount */}
              {advanceAmount > 0 && (
                <tr>
                  <td colSpan={7} className="border border-gray-300 p-1 text-right font-semibold">
                    Balance Amount
                  </td>
                  <td className="border border-gray-300 p-1">
                    ₹ {(Math.round(calculatedTotalAmount) - advanceAmount).toFixed(2)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Amount in Words */}
        <div className="mt-4 border border-gray-300 p-2 text-xs">
          <p>
            <span className="font-semibold">Amount Chargeable (in words):</span> INR{" "}
            {numberToWords(Math.round(calculatedTotalAmount))} Only
          </p>
        </div>

        {/* GST Summary */}
        <div className="mt-4">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-1 text-left">HSN/SAC</th>
                <th className="border border-gray-300 p-1 text-left">Taxable Value</th>
                <th className="border border-gray-300 p-1 text-left">Central Tax</th>
                <th className="border border-gray-300 p-1 text-left">State Tax</th>
                <th className="border border-gray-300 p-1 text-left">Total Tax Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 p-1">3920</td>
                <td className="border border-gray-300 p-1">{subtotal.toFixed(2)}</td>
                <td className="border border-gray-300 p-1">
                  {cgstAmount.toFixed(2)} @ {gstPercentage / 2}%
                </td>
                <td className="border border-gray-300 p-1">
                  {sgstAmount.toFixed(2)} @ {gstPercentage / 2}%
                </td>
                <td className="border border-gray-300 p-1">{gstAmount.toFixed(2)}</td>
              </tr>
              <tr className="bg-gray-100">
                <td className="border border-gray-300 p-1 font-semibold">Total</td>
                <td className="border border-gray-300 p-1">{subtotal.toFixed(2)}</td>
                <td className="border border-gray-300 p-1">{cgstAmount.toFixed(2)}</td>
                <td className="border border-gray-300 p-1">{sgstAmount.toFixed(2)}</td>
                <td className="border border-gray-300 p-1">{gstAmount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Tax Amount in Words */}
        <div className="mt-2 text-xs">
          {/* <p>
            <span className="font-semibold">Tax Amount (in words):</span> INR {numberToWords(Math.round(gstAmount))}{" "}
            Only
          </p> */}
        </div>

        {/* Footer */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="border border-gray-300 p-1">
            <h3 className="font-bold text-xs">Declaration</h3>
            <p className="text-[10px]">
              We declare that this estimate shows the actual price of the goods described and that all particulars are
              true and correct.
            </p>
          </div>
          <div className="border border-gray-300 p-1">
            <h3 className="font-bold text-xs">Company's Bank Details</h3>
            <div className="grid grid-cols-2 gap-1 text-[10px]">
              <p>Bank Name:</p>
              <p>ICICI</p>
              <p>A/c No.:</p>
              <p>657305602279</p>
              <p>Branch & IFS Code:</p>
              <p>ICIC0006573</p>
            </div>
          </div>
        </div>

        {/* Signature */}
        <div className="mt-4 text-right">
          <p className="font-semibold text-xs">for Printverse Pvt Ltd</p>
          <div className="h-6"></div>
          <p className="text-xs">Authorised Signatory</p>
        </div>

        {/* Computer Generated */}
        <div className="mt-2 text-center text-[10px]">
          <p>This is a Computer Generated Estimate</p>
        </div>
      </div>
    </div>
  );
};

export default OrderEstimate;